import { app } from '../common/api/apiClient'
import { API_ROUTES } from '@/common/constants'
import { insertStringVariables } from '@/common/utils/utils'
import type {
    AccountParticipantListResponse,
    AccountSettingsApiResponse,
    CreateAccountPayload,
    RegistrationAddressRequest,
    UpdateAccountSettingsPayload,
} from './type'
import { UserAccount, Account, AccountDetail, AccountUsagePolicies } from '@/common/types'
import type { UpsertAccountParticipantsRequest, UpsertAccountParticipantsResponse } from '@six-invest/api-types'

export async function fetchAccounts() {
    const res = await app.get(`${API_ROUTES.accounts}?includePrimaryAccount=true`)
    return res.data as Account[]
}

export async function getAccountById(accountId: string): Promise<AccountDetail> {
    const res = await app.get(insertStringVariables(API_ROUTES.accountSettings, accountId))
    return res.data as AccountDetail
}

export async function createAccount(data: CreateAccountPayload): Promise<UserAccount> {
    const res = await app.post(API_ROUTES.accounts, data)
    return res.data as UserAccount
}

export async function upsertAccountParticipants(
    accountId: string,
    data: UpsertAccountParticipantsRequest,
): Promise<UpsertAccountParticipantsResponse> {
    const res = await app.post<UpsertAccountParticipantsResponse>(
        insertStringVariables(API_ROUTES.accountsParticipants, accountId),
        data,
    )
    return res.data
}

export async function listAccountParticipants(accountId: string): Promise<AccountParticipantListResponse> {
    const res = await app.get<AccountParticipantListResponse>(
        insertStringVariables(API_ROUTES.accountsParticipants, accountId),
    )
    return res.data
}

export async function updateAccountSettings(
    accountId: string,
    data: UpdateAccountSettingsPayload,
): Promise<{ accountId: string }> {
    const res = await app.put<{ accountId: string }>(insertStringVariables(API_ROUTES.accountSettings, accountId), data)
    return res.data
}

export async function updateTradeConfirmationEmail(
    accountId: string,
    email: string,
): Promise<AccountSettingsApiResponse> {
    const res = await app.post<AccountSettingsApiResponse>(
        insertStringVariables(API_ROUTES.accountTradeConfirmationEmail, accountId),
        { email: email.trim() },
    )
    return res.data
}

export async function updateRegistrationAddress(
    accountId: string,
    address: RegistrationAddressRequest['address'],
): Promise<AccountSettingsApiResponse> {
    const res = await app.post<AccountSettingsApiResponse>(
        insertStringVariables(API_ROUTES.accountRegistrationAddress, accountId),
        { address },
    )
    return res.data
}

export async function confirmAccount(accountId: string): Promise<{ confirmed: boolean }> {
    const res = await app.post<{ confirmed: boolean }>(insertStringVariables(API_ROUTES.accountConfirm, accountId))
    return res.data
}

export async function getAccountUsagePolicies(accountId: string): Promise<AccountUsagePolicies> {
    const res = await app.get(insertStringVariables(API_ROUTES.accountUsagePolicies, accountId))
    return res.data as AccountUsagePolicies
}
